import React, { useState, useEffect, useCallback } from 'react';
import {
  Play, RefreshCw, Database, Upload, Power, Clock, Server, AlertCircle, CheckCircle2, FileSpreadsheet, Lock, Download,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// YYYY-MM-DD for "yesterday" in US/Eastern (the N.A. race date the feed uses).
const yesterdayNA = () => {
  const d = new Date(Date.now() - 24 * 60 * 60 * 1000);
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York' }).format(d);
};

// "Load Sample Feeds" is a dev/testing helper only — hide it on the live site so it
// can't inject sample races into production data. Visible on localhost & preview.
const IS_DEV_ENV = typeof window !== 'undefined' &&
  /(^localhost$|^127\.|preview\.emergentagent\.com$)/.test(window.location.hostname);

const KINDS = ['races', 'entries', 'payouts', 'tracks', 'racepools'];

const GFX_STATUS_STYLES = {
  ok: { cls: 'bg-emerald-900/50 text-emerald-300 border-emerald-700/60', label: 'ok' },
  partial: { cls: 'bg-amber-900/50 text-amber-300 border-amber-700/60', label: 'partial' },
  unmatched: { cls: 'bg-rose-900/50 text-rose-300 border-rose-700/60', label: 'unmatched' },
  pending: { cls: 'bg-slate-700/50 text-slate-300 border-slate-600/60', label: 'pending' },
};

const GfxStatusBadge = ({ status }) => {
  const s = GFX_STATUS_STYLES[status] || GFX_STATUS_STYLES.pending;
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full border text-[11px] font-medium ${s.cls}`} data-testid={`feed-gfx-status-${status}`}>
      {s.label}
    </span>
  );
};

export default function FeedProcessorPanel() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [intervalInput, setIntervalInput] = useState(30);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [previewKind, setPreviewKind] = useState('races');
  const [previewRows, setPreviewRows] = useState([]);
  const [gfxStatus, setGfxStatus] = useState(null);
  const [dumpDate, setDumpDate] = useState(yesterdayNA());
  const [dumping, setDumping] = useState(false);
  const [procLog, setProcLog] = useState([]);
  const [procLogErrorsOnly, setProcLogErrorsOnly] = useState(true);

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const res = await fetch(`${API_URL}/api/feed/status`, { credentials: 'include' });
      if (!res.ok) throw new Error(`Status ${res.status}`);
      const data = await res.json();
      setStatus(data);
      setIntervalInput(data.interval_seconds);
    } catch (e) {
      setErr(`Failed to load feed status: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPreview = useCallback(async (kind) => {
    try {
      const res = await fetch(`${API_URL}/api/feed/data?kind=${kind}&limit=25`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setPreviewRows(data.rows || []);
      }
    } catch (e) {
      /* ignore */
    }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);
  useEffect(() => { fetchPreview(previewKind); }, [previewKind, fetchPreview]);

  const fetchGfxStatus = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/feed/gfx-status`, { credentials: 'include' });
      if (res.ok) setGfxStatus(await res.json());
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => { fetchGfxStatus(); }, [fetchGfxStatus]);

  const fetchProcLog = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/feed/processing-log?limit=200&errors_only=${procLogErrorsOnly}`, { credentials: 'include' });
      if (!res.ok) return;
      const data = await res.json();
      setProcLog(data.log || []);
    } catch { /* non-fatal */ }
  }, [procLogErrorsOnly]);
  useEffect(() => { fetchProcLog(); }, [fetchProcLog]);

  // Live auto-refresh every 15s so the feed status, counts, files-waiting and gfx
  // coverage stay current without manually clicking refresh.
  useEffect(() => {
    const id = setInterval(() => { fetchStatus(); fetchGfxStatus(); fetchProcLog(); }, 15000);
    return () => clearInterval(id);
  }, [fetchStatus, fetchGfxStatus, fetchProcLog]);

  const toggleEnabled = async () => {
    if (!status) return;
    setErr(''); setMsg('');
    try {
      const next = !status.enabled;
      const res = await fetch(`${API_URL}/api/feed/config?enabled=${next}`, {
        method: 'PUT', credentials: 'include',
      });
      if (!res.ok) throw new Error(`Status ${res.status}`);
      setMsg(next ? 'Feed processor enabled.' : 'Feed processor paused.');
      fetchStatus();
    } catch (e) {
      setErr(`Toggle failed: ${e.message}`);
    }
  };

  const toggleFtpDelete = async () => {
    if (!status) return;
    const next = !status.ftp_delete;
    if (next && !window.confirm('Turn ON delete-after-read? The processor will DELETE each file from the vendor FTP folder once it has read it. This drains the backlog but is destructive on the live feed. Continue?')) return;
    setErr(''); setMsg('');
    try {
      const res = await fetch(`${API_URL}/api/feed/config?ftp_delete=${next}`, {
        method: 'PUT', credentials: 'include',
      });
      if (!res.ok) throw new Error(`Status ${res.status}`);
      setMsg(next ? 'Delete-after-read ON — FTP folder will drain as files are read.' : 'Delete-after-read OFF — files kept on FTP.');
      fetchStatus();
    } catch (e) {
      setErr(`Toggle failed: ${e.message}`);
    }
  };

  const toggleHotExacta = async () => {
    if (!status) return;
    setErr(''); setMsg('');
    try {
      const next = !status.hot_exacta_enabled;
      const res = await fetch(`${API_URL}/api/feed/config?hot_exacta_enabled=${next}`, {
        method: 'PUT', credentials: 'include',
      });
      if (!res.ok) throw new Error(`Status ${res.status}`);
      setMsg(next ? 'Hot Exacta capture enabled.' : 'Hot Exacta capture turned OFF.');
      fetchStatus();
    } catch (e) {
      setErr(`Toggle failed: ${e.message}`);
    }
  };

  const saveInterval = async () => {
    setErr(''); setMsg('');
    try {
      const res = await fetch(`${API_URL}/api/feed/config?interval_seconds=${intervalInput}`, {
        method: 'PUT', credentials: 'include',
      });
      if (!res.ok) throw new Error(`Status ${res.status}`);
      setMsg(`Poll interval set to ${intervalInput}s.`);
      fetchStatus();
    } catch (e) {
      setErr(`Save failed: ${e.message}`);
    }
  };

  const loadSamples = async () => {
    setErr(''); setMsg('');
    try {
      const res = await fetch(`${API_URL}/api/feed/load-samples`, { method: 'POST', credentials: 'include' });
      const data = await res.json();
      setMsg(`Loaded ${data.loaded} sample files into inbox.`);
      fetchStatus();
    } catch (e) {
      setErr(`Load samples failed: ${e.message}`);
    }
  };

  const runNow = async () => {
    setRunning(true); setErr(''); setMsg('');
    try {
      const res = await fetch(`${API_URL}/api/feed/run-now`, { method: 'POST', credentials: 'include' });
      if (!res.ok) throw new Error(`Status ${res.status}`);
      const data = await res.json();
      const s = data.summary || {};
      setMsg(`Run complete: ${s.processed || 0} processed (${s.cycle || 0} cycle, ${s.prices || 0} prices, ${s.willpays || 0} willpays, ${s.discarded || 0} discarded, ${s.error || 0} errors).`);
      fetchStatus();
      fetchPreview(previewKind);
      fetchProcLog();
    } catch (e) {
      setErr(`Run failed: ${e.message}`);
    } finally {
      setRunning(false);
    }
  };

  const downloadDump = async () => {
    setDumping(true); setErr(''); setMsg('');
    try {
      const res = await fetch(`${API_URL}/api/feed/export-dump?date=${dumpDate}`, { credentials: 'include' });
      if (!res.ok) throw new Error(`Status ${res.status}`);
      const counts = res.headers.get('X-Dump-Counts') || '';
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `intellitix_dump_${dumpDate}.json.gz`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setMsg(`Dump downloaded for ${dumpDate}. ${counts}`);
    } catch (e) {
      setErr(`Dump failed: ${e.message}`);
    } finally {
      setDumping(false);
    }
  };

  const stats = status?.stats || {};
  const lastRun = status?.last_run || {};
  const lock = status?.worker_lock || {};

  return (
    <div className="space-y-6" data-testid="feed-processor-panel">
      {/* Messages */}
      {msg && (
        <div className="bg-emerald-500/10 border border-emerald-500/50 text-emerald-400 px-4 py-3 rounded-lg flex items-center gap-2" data-testid="feed-success">
          <CheckCircle2 size={16} /> {msg}
        </div>
      )}
      {err && (
        <div className="bg-red-500/10 border border-red-500/50 text-red-400 px-4 py-3 rounded-lg flex items-center gap-2" data-testid="feed-error">
          <AlertCircle size={16} /> {err}
        </div>
      )}

      {/* Control card */}
      <div className="bg-slate-800/60 border border-slate-700 rounded-xl p-6">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Server size={18} className="text-emerald-400" /> Feed Processor
          </h3>
          <button
            onClick={fetchStatus}
            className="text-slate-400 hover:text-white transition"
            data-testid="feed-refresh-btn"
            title="Refresh"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
          {/* Enabled toggle */}
          <div className="bg-slate-900/60 rounded-lg p-4 border border-slate-700">
            <div className="text-xs text-slate-400 mb-2 uppercase tracking-wide">Worker</div>
            <button
              onClick={toggleEnabled}
              data-testid="feed-toggle-btn"
              className={`w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-medium transition ${
                status?.enabled
                  ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                  : 'bg-slate-700 hover:bg-slate-600 text-slate-200'
              }`}
            >
              <Power size={16} />
              {status?.enabled ? 'Enabled (click to pause)' : 'Paused (click to enable)'}
            </button>
            <button
              onClick={toggleHotExacta}
              data-testid="feed-hot-exacta-toggle-btn"
              title="Per-cycle Hot Exacta capture. Turn OFF to relieve the feed processor if it stalls."
              className={`w-full mt-2 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition ${
                status?.hot_exacta_enabled
                  ? 'bg-violet-700 hover:bg-violet-800 text-white'
                  : 'bg-orange-500 hover:bg-orange-400 text-white'
              }`}
            >
              <Power size={14} />
              {status?.hot_exacta_enabled ? 'Hot Exacta: ON (click to kill)' : 'Hot Exacta: OFF (click to enable)'}
            </button>
          </div>

          {/* Interval */}
          <div className="bg-slate-900/60 rounded-lg p-4 border border-slate-700">
            <div className="text-xs text-slate-400 mb-2 uppercase tracking-wide flex items-center gap-1">
              <Clock size={12} /> Poll interval (sec)
            </div>
            <div className="flex gap-2">
              <input
                type="number"
                min="5"
                value={intervalInput}
                onChange={(e) => setIntervalInput(Number(e.target.value))}
                className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm"
                data-testid="feed-interval-input"
              />
              <button
                onClick={saveInterval}
                className="px-3 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm"
                data-testid="feed-interval-save-btn"
              >
                Save
              </button>
            </div>
            {status?.source === 'ftp' && (
              <div className="text-xs text-slate-400 mt-3 pt-3 border-t border-slate-700/60" data-testid="feed-ftp-last-poll">
                Last vendor poll:{' '}
                <span className="text-white font-semibold">{status?.last_run?.ftp_found ?? '—'}</span> found in folder
                {' · '}
                <span className="text-white font-semibold">{status?.last_run?.ftp_pulled ?? 0}</span> pulled
                {(() => {
                  const ms = status?.last_run?.ftp_fetch_ms;
                  const pulled = status?.last_run?.ftp_pulled ?? 0;
                  const bytes = status?.last_run?.ftp_bytes ?? 0;
                  if (!ms || pulled <= 0) return null;
                  const secs = ms / 1000;
                  const fps = (pulled / secs).toFixed(1);
                  const kbps = (bytes / 1024 / secs).toFixed(0);
                  return (
                    <span data-testid="feed-ftp-speed">
                      {' in '}<span className="text-white font-semibold">{secs.toFixed(1)}s</span>
                      {' — '}<span className="text-emerald-400 font-semibold">{fps} files/s</span>
                      {' · '}<span className="text-emerald-400 font-semibold">{kbps} KB/s</span>
                    </span>
                  );
                })()}
              </div>
            )}
          </div>

          {/* Source */}
          <div className="bg-slate-900/60 rounded-lg p-4 border border-slate-700">
            <div className="text-xs text-slate-400 mb-2 uppercase tracking-wide">Source</div>
            <div className="text-sm text-white font-medium" data-testid="feed-source">
              {status?.source === 'ftp' ? 'FTP (configured)' : 'Local inbox'}
            </div>
            <div className="text-xs text-slate-400 mt-1">
              Inbox: {status?.inbox_count ?? 0} file(s) waiting
            </div>
            {status?.source === 'ftp' && (
              <button
                onClick={toggleFtpDelete}
                data-testid="feed-ftp-delete-toggle-btn"
                title="When ON, each file is deleted from the vendor FTP folder after it is read, so the folder drains. When OFF, files are kept on the server (folder grows)."
                className={`w-full mt-2 flex items-center justify-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition ${
                  status?.ftp_delete
                    ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                    : 'bg-orange-500 hover:bg-orange-400 text-white'
                }`}
              >
                {status?.ftp_delete ? 'Delete after read: ON' : 'Delete after read: OFF (folder grows)'}
              </button>
            )}
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex flex-wrap gap-3">
          <button
            onClick={runNow}
            disabled={running}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded-lg font-medium transition"
            data-testid="feed-run-now-btn"
          >
            <Play size={16} className={running ? 'animate-pulse' : ''} />
            {running ? 'Running…' : 'Run Now'}
          </button>
          <button
            onClick={loadSamples}
            className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg font-medium transition"
            data-testid="feed-load-samples-btn"
            style={{ display: IS_DEV_ENV ? undefined : 'none' }}
          >
            <Upload size={16} /> Load Sample Feeds
          </button>
          <div className="flex items-center gap-2 ml-auto">
            <span className="text-xs text-slate-400 uppercase tracking-wide hidden sm:inline">Data dump</span>
            <input
              type="date"
              value={dumpDate}
              onChange={(e) => setDumpDate(e.target.value)}
              className="bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm [&::-webkit-calendar-picker-indicator]:invert [&::-webkit-calendar-picker-indicator]:opacity-70"
              data-testid="feed-dump-date"
            />
            <button
              onClick={downloadDump}
              disabled={dumping || !dumpDate}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white rounded-lg font-medium transition"
              data-testid="feed-dump-btn"
              title="Download a gzipped JSON dump of this date's feed data"
            >
              <Download size={16} className={dumping ? 'animate-pulse' : ''} />
              {dumping ? 'Preparing…' : 'Download Data Dump'}
            </button>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="bg-slate-800/60 border border-slate-700 rounded-xl p-6">
        <h3 className="text-sm font-semibold text-slate-300 mb-1 flex items-center gap-2 uppercase tracking-wide">
          <Database size={16} className="text-emerald-400" /> Ingested Data
        </h3>
        <p className="text-xs text-slate-400 mb-4" data-testid="feed-ingested-date">
          Current N.A. race date{stats.date ? <>: <span className="text-slate-200 font-medium">{stats.date}</span></> : ''} · recent feed only
        </p>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {['tracks', 'races', 'entries', 'racepools', 'payouts'].map((k) => (
            <div key={k} className="bg-slate-900/60 rounded-lg p-4 text-center border border-slate-700" data-testid={`feed-stat-${k}`}>
              <div className="text-2xl font-bold text-white">{stats[k] ?? 0}</div>
              <div className="text-xs text-slate-400 capitalize mt-1">{k}</div>
            </div>
          ))}
        </div>

        {lastRun.last_run && (
          <div className="mt-4 text-xs text-slate-400">
            Last run: <span className="text-slate-300">{new Date(lastRun.last_run).toLocaleString()}</span>
            {' · '}{lastRun.run_type || '—'}
            {lastRun.summary && (
              <> {' · '}{lastRun.summary.processed || 0} processed, {lastRun.summary.error || 0} errors</>
            )}
          </div>
        )}

        {/* Cross-replica worker lock — in production 2 replicas run; only one holds
            the lock and runs feed passes at a time. */}
        <div
          className="mt-4 flex flex-wrap items-center gap-2 text-xs bg-slate-900/60 border border-slate-700 rounded-lg px-3 py-2.5"
          data-testid="feed-worker-lock"
        >
          <Lock size={13} className={lock.active ? 'text-emerald-400' : 'text-slate-500'} />
          <span className="text-slate-400">Feed worker lock:</span>
          {lock.active ? (
            <span
              className={`px-1.5 py-0.5 rounded font-semibold ${
                lock.held_by_self
                  ? 'bg-emerald-900/60 text-emerald-300 border border-emerald-700/50'
                  : 'bg-amber-900/50 text-amber-300 border border-amber-700/50'
              }`}
              data-testid="feed-worker-lock-state"
            >
              {lock.held_by_self ? 'HELD BY THIS REPLICA' : `HELD BY REPLICA ${lock.holder}`}
            </span>
          ) : (
            <span className="px-1.5 py-0.5 rounded font-semibold bg-slate-800 text-slate-400 border border-slate-700" data-testid="feed-worker-lock-state">
              IDLE (free)
            </span>
          )}
          <span className="text-slate-500">· this replica: <span className="text-slate-300">{lock.this_replica || '—'}</span></span>
          {lock.active && lock.expires_at && (
            <span className="text-slate-500">· expires {new Date(lock.expires_at).toLocaleTimeString()}</span>
          )}
        </div>
      </div>

      {/* Data preview */}
      <div className="bg-slate-800/60 border border-slate-700 rounded-xl p-6">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-sm font-semibold text-slate-300 uppercase tracking-wide">
            Recently Processed
          </h3>
          <select
            value={previewKind}
            onChange={(e) => setPreviewKind(e.target.value)}
            className="bg-slate-900 border border-slate-600 rounded-lg px-3 py-1.5 text-white text-sm"
            data-testid="feed-preview-kind"
          >
            {KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
        </div>
        <p className="text-xs text-slate-500 mb-3">
          The {previewKind} most recently written by the processor — newest first (by processed time).
          <button onClick={() => fetchPreview(previewKind)} className="ml-2 text-emerald-400 hover:underline" data-testid="feed-preview-refresh">refresh</button>
        </p>
        <div className="overflow-x-auto">
          {previewRows.length === 0 ? (
            <div className="text-slate-500 text-sm py-6 text-center">No rows yet.</div>
          ) : (() => {
            const HIDE = new Set(['created_at', 'updated_at', 'gfx', 'extra', 'cancelled']);
            // For races, pin MTP + Zero-Cycle Count (and freeze state) so they're always visible.
            const PINNED = {
              races: ['race_key', 'race_number', 'status', 'mtp', 'mtp0_count', 'flags_frozen', 'final'],
            };
            const LABELS = { mtp: 'MTP', mtp0_count: 'ZeroCycleCnt', flags_frozen: 'Frozen', race_number: 'race' };
            const cols = PINNED[previewKind] || Object.keys(previewRows[0]).filter((c) => !HIDE.has(c)).slice(0, 7);
            const fmt = (t) => t ? new Date(t).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '—';
            const isQH = (row) => row.is_qh === true || (typeof row.distance_raw === 'string' && /yard/i.test(row.distance_raw) && !/furlong|mile/i.test(row.distance_raw));
            const cellVal = (row, col) => {
              if (col === 'mtp0_count') return row.mtp0_count ?? 0; // never-set => 0
              if (col === 'flags_frozen') return row.flags_frozen ? 'yes' : 'no';
              return String(row[col] ?? '');
            };
            return (
              <table className="w-full text-xs" data-testid="feed-preview-table">
                <thead>
                  <tr className="text-left text-slate-400 border-b border-slate-700">
                    <th className="py-2 pr-4 font-medium text-emerald-400">processed</th>
                    {cols.map((col) => <th key={col} className="py-2 pr-4 font-medium">{LABELS[col] || col}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {previewRows.map((row, i) => {
                    const scratched = previewKind === 'entries' && row.scratched;
                    return (
                    <tr key={i} className={`border-b border-slate-800 ${scratched ? 'line-through text-slate-500 opacity-70' : 'text-slate-300'}`} data-testid={scratched ? 'feed-preview-row-scratched' : 'feed-preview-row'}>
                      <td className="py-2 pr-4 whitespace-nowrap text-emerald-300/80">{fmt(row.updated_at)}</td>
                      {cols.map((col) => (
                        <td key={col} className={`py-2 pr-4 whitespace-nowrap ${col === 'mtp0_count' && (row.mtp0_count ?? 0) >= 3 ? 'text-emerald-400 font-semibold' : ''}`}>
                          {cellVal(row, col)}
                          {previewKind === 'races' && col === 'race_number' && isQH(row) && (
                            <span className="ml-2 px-1.5 py-0.5 text-[10px] font-bold rounded bg-orange-500/20 text-orange-400 border border-orange-500/40" data-testid="feed-qh-badge" title={`Quarter Horse race${row.distance_raw ? ` (${row.distance_raw})` : ''} — excluded from TP/ROI stats`}>QH</span>
                          )}
                        </td>
                      ))}
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            );
          })()}
        </div>
      </div>
      {/* Handicapping (gfx) data — FTP auto-ingest */}
      <div className="bg-slate-800/60 border border-slate-700 rounded-xl p-6" data-testid="feed-gfx-card">
        <h3 className="text-sm font-semibold text-slate-300 mb-1 flex items-center gap-2 uppercase tracking-wide">
          <FileSpreadsheet size={16} className="text-emerald-400" /> Handicapping Data (CSV)
        </h3>
        <p className="text-xs text-slate-400 mb-4">
          Handicapping CSVs are ingested <span className="text-slate-200">automatically from the FTP folder</span> by
          the feed worker. Rows match entries by feed event code (preferred) or DRF track code, then date, race &
          program — applied the moment those races are populated.
        </p>

        <div className="flex items-center gap-2 mb-4 rounded-lg bg-slate-900/60 border border-slate-700 px-3 py-2 text-xs text-slate-400" data-testid="feed-gfx-upload-disabled">
          <Lock size={14} className="text-slate-500" />
          Manual upload is disabled — drop the <span className="text-slate-200">.csv</span> in the FTP folder.
        </div>

        {gfxStatus && (
          <div className="text-xs text-slate-400 mb-3" data-testid="feed-gfx-status">
            Staged: <span className="text-slate-200 font-medium">{gfxStatus.rows || 0}</span> rows
            {gfxStatus.dates?.length ? ` · dates: ${gfxStatus.dates.join(', ')}` : ''}
            {gfxStatus.tracks?.length ? ` · ${gfxStatus.tracks.length} tracks` : ''}
          </div>
        )}

        {gfxStatus?.coverage?.length > 0 && (
          <div className="space-y-2" data-testid="feed-gfx-coverage">
            {gfxStatus.coverage.some((c) => c.status === 'unmatched') && (
              <div className="flex items-start gap-2 rounded-lg bg-rose-950/40 border border-rose-800/60 px-3 py-2 text-xs text-rose-300" data-testid="feed-gfx-unmatched-warning">
                <AlertCircle size={14} className="mt-0.5 shrink-0" />
                <span>
                  Some tracks have races in the feed but received <b>no</b> handicapping — likely a wrong/missing
                  event code in the CSV. Check the <b>unmatched</b> rows below.
                </span>
              </div>
            )}
            <div className="overflow-x-auto rounded-lg border border-slate-700">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-slate-900/70 text-slate-400 text-left">
                    <th className="px-3 py-2 font-medium">Date</th>
                    <th className="px-3 py-2 font-medium">Track</th>
                    <th className="px-3 py-2 font-medium">Match</th>
                    <th className="px-3 py-2 font-medium text-right">Staged</th>
                    <th className="px-3 py-2 font-medium text-right">In Feed</th>
                    <th className="px-3 py-2 font-medium text-right">Applied</th>
                    <th className="px-3 py-2 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {gfxStatus.coverage.map((c, i) => (
                    <tr key={`${c.date}-${c.code}-${i}`} className="border-t border-slate-800 text-slate-300" data-testid={`feed-gfx-cov-row-${c.code}`}>
                      <td className="px-3 py-2 whitespace-nowrap">{c.date}</td>
                      <td className="px-3 py-2 font-medium text-slate-200">{c.code}</td>
                      <td className="px-3 py-2 text-slate-400">{c.match_by === 'event' ? 'event' : 'DRF'}</td>
                      <td className="px-3 py-2 text-right">{c.staged}</td>
                      <td className="px-3 py-2 text-right">{c.entries_in_feed}</td>
                      <td className="px-3 py-2 text-right">{c.applied}</td>
                      <td className="px-3 py-2">
                        <GfxStatusBadge status={c.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Processing Log — TTL-capped history of file ingests (14 days). */}
      <div className="bg-slate-800/40 border border-slate-700 rounded-xl p-5" data-testid="feed-proc-log">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-slate-200">Processing Log</h3>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setProcLogErrorsOnly((v) => !v)}
              data-testid="feed-proc-log-errors-toggle"
              className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${procLogErrorsOnly
                ? 'bg-rose-950/50 border-rose-800/60 text-rose-300'
                : 'bg-slate-800 border-slate-600 text-slate-300 hover:bg-slate-700'}`}>
              {procLogErrorsOnly ? 'Errors only' : 'Showing all'}
            </button>
            <button onClick={fetchProcLog} className="text-xs text-slate-400 hover:text-slate-200" data-testid="feed-proc-log-refresh">Refresh</button>
          </div>
        </div>
        <p className="text-xs text-slate-400 mb-3">
          {procLogErrorsOnly
            ? <>Showing only <span className="text-rose-300">errors &amp; discards</span> (newest first) — the files the worker rejected or couldn't process. Toggle to see every file.</>
            : <>Every file the worker touches (newest first). Watch the <span className="text-rose-300">error</span> rows to catch feed problems — e.g. a rejected handicapping CSV.</>}
        </p>
        {procLog.length === 0 ? (
          <div className="text-xs text-slate-500 py-4 text-center" data-testid="feed-proc-log-empty">{procLogErrorsOnly ? 'No errors or discards recorded.' : 'No processing activity recorded yet.'}</div>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-slate-700 max-h-96 overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="sticky top-0">
                <tr className="bg-slate-900/90 text-slate-400 text-left">
                  <th className="px-3 py-2 font-medium">Time</th>
                  <th className="px-3 py-2 font-medium">Run</th>
                  <th className="px-3 py-2 font-medium">File</th>
                  <th className="px-3 py-2 font-medium">Action</th>
                  <th className="px-3 py-2 font-medium text-right">Rows</th>
                  <th className="px-3 py-2 font-medium text-right">Staged</th>
                  <th className="px-3 py-2 font-medium text-right">Applied</th>
                  <th className="px-3 py-2 font-medium">Detail</th>
                </tr>
              </thead>
              <tbody>
                {procLog.map((r, i) => {
                  const isErr = r.action === 'error' || r.action === 'discarded' || !!r.reason;
                  return (
                    <tr key={i} className="border-t border-slate-800 text-slate-300" data-testid={`feed-proc-log-row-${i}`}>
                      <td className="px-3 py-2 whitespace-nowrap text-slate-400">{r.ts ? new Date(r.ts).toLocaleString() : '—'}</td>
                      <td className="px-3 py-2 text-slate-400">{r.run_type || '—'}</td>
                      <td className="px-3 py-2 font-mono text-slate-200 max-w-[220px] truncate" title={r.file || ''}>{r.file || '—'}</td>
                      <td className="px-3 py-2">
                        <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium border ${isErr ? 'bg-rose-950/50 border-rose-800/60 text-rose-300' : 'bg-emerald-950/40 border-emerald-800/50 text-emerald-300'}`}>
                          {r.action || '—'}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right">{r.rows ?? '—'}</td>
                      <td className="px-3 py-2 text-right">{r.staged ?? '—'}</td>
                      <td className="px-3 py-2 text-right">{r.applied ?? '—'}</td>
                      <td className="px-3 py-2 text-slate-400 max-w-[280px] truncate" title={r.reason || ''}>{r.reason || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

    </div>
  );
}
